import {currentTimestamp} from "../utils/date-time"
import {plainToInstance} from "class-transformer"
import {MAX_TEAM_PLAYERS, PLAY_TIMES, EXTRA_PLAY_TIME} from "../variables"

export class Team {
    _id!: string | undefined
    name = ""
    email = "" // in case no players have an email
    locale = "fr"
    // is a team actice in the venue
    active = true
    // admins can always login
    isAdmin = false
    /* eslint-disable no-use-before-define */
    players: Array<Player> = []
    // moment of registration
    registerTime = 0
    // has the team completed the registration
    registrationCompleted = false
    // time team entered venue
    startTime = 0
    // available play time in seconds
    playTime = 3600
    // the game the team is currently playing
    currentGame: string | undefined
    difficulty = Difficulty.Normal
    boosters = 0
    gameplays: Array<GamePlay> = []
    outroCompleted = false
    // for online bookings
    bookingId: string | undefined
    beforeIntroRoom = false
    inIntroRoom = false
    introCompleted = false
    didEntryCheck = false
    bonusGameId = ""
    additionalInfo = {} as any

    public get endTime(): number {
        return this.startTime + this.playTime + EXTRA_PLAY_TIME
    }

    public get canEnterVenue(): boolean {
        if (this.isAdmin) {
            return true
        }
        const now = currentTimestamp()
        return now >= this.startTime && now <= this.endTime
    }

    public get remainingTime(): number {
        return Math.max(0, this.endTime - currentTimestamp())
    }

    public get lastPlayedGame(): GamePlay | undefined {
        const gameplays = this.gameplays.sort((x, y) => y.insertAt > x.insertAt ? 1 : -1)
        return gameplays.length > 0 ? gameplays[0] : undefined
    }

    public static toClass(data: any): Team {
        if (data !== undefined) {
            const team = plainToInstance(Team, data)
            team._id = data._id
            return team
        }
        return new Team()
    }

    public static toClassArray(teamData: Array<any>): Array<Team> {
        return teamData.map(team => this.toClass(team))
    }
}

export class Booking {
    _id!: string | undefined
    barcode: string | undefined = undefined
    active: boolean = true
    bookingId: string | undefined = undefined // qweekle booking id
    startTime: number = 0
    startDate: string = ""
    playTime: number = 0
    name: string = ""
    email: string = ""
    locked: boolean = false
    // set to true if the user created teams using this booking
    usedBooking: boolean = false
    playerCount: number = 5
    createdAt: Date = new Date()

    public get teamCount(): number {
        return Math.ceil(this.playerCount / MAX_TEAM_PLAYERS)
    }

    public static toClass(data: any): Booking {
        const booking = plainToInstance(Booking, data)
        booking._id = data._id
        return booking
    }

    public static toClassArray(bookingData: Array<any>): Array<Booking> {
        return bookingData.map(booking => this.toClass(booking))
    }
}

export class Player {
    _id!: string | undefined
    name = ""
    rfid = ""
    email = ""

    public static createWithName(name: string) {
        const player = new Player()
        player.name = name
        return player
    }

    public static toClass(data: any): Player {
        const player = plainToInstance(Player, data)
        player._id = data._id
        return player
    }
}

export class GamePlay {
    gameId: string | undefined
    score: number = 0
    percentage: number = 0
    playTime: number = 0
    insertAt: number = 0
    difficulty: Difficulty = Difficulty.Normal
    playerScores: Array<PlayerScore> = []
}

export class PlayerScore {
    playerId: string = ""
    name: string = ""
    score: number = 0
    percentage: number = 0
    playTime: number = 0
}

export enum AdminUserRole {
    Admin = "admin",
    Operator = "operator",
}
export class AdminUser {
    _id!: string | undefined
    username = ""
    hash = ""
    role = AdminUserRole.Operator

    constructor(username: string, hash: string, role: AdminUserRole) {
        this.username = username
        this.hash = hash
        this.role = role
    }
}

/*
    id: id used to identify a device in code
    debugkey: key used to simulate the device in development
    moxaId: combination of ip address of the moxa controller together with the specific id for
            the channel this device is on, the specific id is set on the snmp settings on the
            moxa controller, for analog and output devices we just use prefix a and o and the
            channel number
            examples: 192.168.127.101:4, 192.168.127.101:a0, 192.168.127.101:o1
    moxaIp: the ip of the moxa controller (only used for output devices)
    outputRegister: this is the index of the digital output channel you are pushing to (starts with 1)
                    only set this value if this is an output device
*/
export interface Device {
    id: string
    debugkey: string
    moxaId: string
    outputRegister: number
    moxaIp: string
}

export class AppState {
    public _id!: string
    public nextTeamNumber = 1
    // used to keep track of the daily checkup to archive teams
    public lastDayHash = 0
    public settings: AppSettings = new AppSettings()

    public static toClass(data: any): AppState {
        const appState = plainToInstance(AppState, data)
        appState._id = data._id
        return appState
    }
}

export enum PaymentMode {
    Normal = "normal",
    Technical = "technical",
    Free = "free",
}

export class AppSettings {
    public _id!: string
    public closingTime: number
    public ignoreClosingTime: boolean = false
    public prices: Array<SettingsPrice>
    public paymentMode: PaymentMode = PaymentMode.Normal
    public paymentEmails: string[] = []
    public tax: number = 10

    constructor() {
        this.closingTime = 17 * 60 * 60
        this.prices = []
        for (const [i, playTime] of PLAY_TIMES.entries()) {
            this.prices.push(new SettingsPrice(10 * (i + 1), playTime))
        }
    }

    public get formattedClosingTime(): string {
        const hours = Math.floor(this.closingTime / 3600)
        const minutes = Math.floor((this.closingTime - hours * 3600) / 60)
        return `${hours}:${minutes.toString().padStart(2, "0")}`
    }

    public parseClosingTime(time: string) {
        const parts = time.split(":")
        if (parts.length === 2) {
            const hours = parseInt(parts[0])
            const minutes = parseInt(parts[1])
            if (!isNaN(hours) && !isNaN(minutes)) {
                this.closingTime = hours * 3600 + minutes * 60
            }
        }
    }

    public static toClass(data: any): AppSettings {
        const settings = plainToInstance(AppSettings, data)
        settings._id = data._id
        return settings
    }
}

export class SettingsPrice {
    public price: number
    public playTime: number

    constructor(price: number, playTime: number) {
        this.price = price
        this.playTime = playTime
    }
}

// used to send socket messages between 2 game screens
export class GameSocketMessage {
    public gameId: string | undefined
    public message: string = ""
    public data: any = {}

    constructor(gameId: string, message: string, data: any = {}) {
        this.gameId = gameId
        this.message = message
        this.data = data
    }
}

export interface DigitalInputMessage {
    device: Device
    active: boolean
}

export class HiScore {
    public name = ""
    public score = 0
}

export class Room {
    public _id!: string
    public roomId = ""
    public name = ""
    public isGame: boolean = false
    public isOutputScreen: boolean = false
    public maintenance: boolean = false
    public showMaintenance: boolean = true
    public sortId = 0

    public static toClass(data: any): Room {
        const room = plainToInstance(Room, data)
        room._id = data._id
        return room
    }

    public static toClassArray(roomData: Array<any>): Array<Room> {
        return roomData.map(room => this.toClass(room))
    }
}

export enum RoomStatus {
    Unknown = -2,
    Free = 0,
    Occupied = 1,
    Maintenance = -1
}

/**
 * API
 */
export class APIResponse {
    public status: string = ""
}

export class APICreateResponse {
    public insertedId: string = ""
}

export class APICreateManyResponse {
    public insertedIds: Array<string> = []
}

export enum Difficulty {
    Easy = 0,
    Normal = 1,
    Hard = 2
}
